import { useState } from 'react';
import { Box, Divider, withStyles } from '@material-ui/core';
import { SButton, SText } from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import {
  GridPricing,
  Product,
  ProductGridItem,
  ProductPriceType
} from '@setvi/shared/interfaces';
import { ProductLabel, PromotionBadge } from 'componentsV2';
import { PriceButton, Stock } from 'pages/products/components';
import { Customer } from 'Services/Mongo/Types';
import {
  FullPrice,
  ListPrice
} from 'pages/products/components/products-list/components/product/components';
import { Statuses } from '../../../list/components/product-card/grid/components';

const BoxWithMargin = withStyles(({ spacing }) => ({
  root: {
    marginTop: spacing(2)
  }
}))(Box);

interface ProductInfoProps {
  product: Product;
  gridConfig: ProductGridItem;
  customer: Customer;
  priceType: ProductPriceType;
  productPricesLoading: boolean;
  pricingList: GridPricing[];
  hideManufacturer?: boolean;
  onGetProductPrice?: (product: Product) => Promise<void>;
  onStockClick: (product: Product) => void;
  onPromotionClick: (product: Product) => void;
}

export const ProductInformation = ({
  product,
  priceType,
  gridConfig,
  customer,
  pricingList,
  productPricesLoading,
  hideManufacturer = false,
  onGetProductPrice,
  onStockClick,
  onPromotionClick
}: ProductInfoProps) => {
  const [singlePriceLoading, setSinglePriceLoading] = useState(false);

  const getLeadTime = () => {
    const leadTime = product?.custom?.leadTime as
      | string
      | { value: string }
      | undefined;

    if (typeof leadTime === 'string') return `${leadTime} Days`;
    if (leadTime?.value) return `${leadTime.value} Days`;
    return product?.custom?.ship_lead_time__business_days || '-';
  };

  return (
    <Box p="20px">
      <SText title={product.name} size="lg" weight="semibold" noLineClamp />
      <Box display="flex" flexDirection="column" py={2}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gridGap="4px">
          <SText
            title={`SKU: ${product.sku}`}
            size="sm"
            fontColor={TextColor.Grey}
          />
          {gridConfig?.stock?.show && (
            <Stock
              variant="body2"
              style={{ minWidth: 'max-content' }}
              inventory={product.inventory}
              isDropship={product.dropship}
              stockInfo={product?.stockInfo}
              isStockProduct={product?.isStockProduct}
              // Details view: drop the value entirely rather than show a lone
              // hyphen, matching the desktop product details header.
              nonStockedDisplay="hide"
              selectedCustomer={customer}
              onClick={() => onStockClick(product)}
            />
          )}
        </Box>
        {gridConfig?.manufacturerLeadTime && (
          <SText title={`Lead time: ${getLeadTime()}`} size="sm" />
        )}
      </Box>
      <Divider />
      {!hideManufacturer && (
        <>
          <Box
            py="12px"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gridGap="8px">
            <Box display="flex" alignItems="center" gridGap="4px">
              <SText
                size="sm"
                title={`MFG: ${product?.vendor?.name || 'Not Available'}`}
              />
              {product.privateLabel && <ProductLabel />}
              {product?.vendorHierarchy > 0 && (
                <ProductLabel type="strategic-vendor" />
              )}
              {product?.promotions?.length > 0 && (
                <PromotionBadge onClick={() => onPromotionClick(product)} />
              )}
            </Box>
            {gridConfig?.manufacturerSku && product?.manufacturerSku && (
              <SText
                title={product?.manufacturerSku}
                size="sm"
                fontColor={TextColor.SlateGrey}
                noLineClamp
              />
            )}
          </Box>
          <Divider />
        </>
      )}

      {priceType === ProductPriceType.Full && !!pricingList.length && (
        <Box mt={2}>
          <FullPrice
            fontSize="sm"
            product={product}
            loading={productPricesLoading}
            pricing={pricingList}
          />
          <Divider style={{ marginTop: 8 }} />
        </Box>
      )}
      {priceType === ProductPriceType.List && (
        <Box mt={2}>
          <ListPrice
            product={{
              ...product,
              custom: {
                ...product.custom,
                ship_lead_time__business_days:
                  // @ts-ignore
                  product?.custom?.ship_lead_time__business_days?.value
              }
            }}
            fontSize="sm"
          />
          <Divider style={{ marginTop: 8 }} />
        </Box>
      )}
      {priceType === ProductPriceType.Get && customer && (
        <Box mt={2}>
          <Box display="flex" justifyContent="center" height={40}>
            <PriceButton
              price={product.price}
              onGetPrice={() => onGetProductPrice(product)}
            />
          </Box>
          <Divider style={{ marginTop: 8 }} />
        </Box>
      )}
      {priceType === ProductPriceType.GetFull && customer && (
        <Box mt={2}>
          <Box display="flex" justifyContent="center">
            {product.price ? (
              <FullPrice
                fontSize="sm"
                product={product}
                pricing={pricingList}
              />
            ) : (
              <SButton
                loading={singlePriceLoading}
                onClick={async e => {
                  e.stopPropagation();
                  setSinglePriceLoading(true);
                  await onGetProductPrice(product);
                  setSinglePriceLoading(false);
                }}
                style={{ width: 'max-content' }}
                variant="outlined">
                Get Price
              </SButton>
            )}
          </Box>
          <Divider style={{ marginTop: 8 }} />
        </Box>
      )}
      <BoxWithMargin>
        <Statuses product={product} />
      </BoxWithMargin>
    </Box>
  );
};
